import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../../components/AppIcon';

const PasswordRecoveryRequest = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const accountType = location?.state?.accountType || 'user'; // 'user' or 'business'
  
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!email?.trim()) {
      setError('Por favor ingresa tu correo electrónico');
      return;
    }

    if (!validateEmail(email)) {
      setError('Por favor ingresa un correo electrónico válido');
      return;
    }

    setIsLoading(true);

    // Simulate API call to send verification code
    setTimeout(() => {
      setIsLoading(false);
      setShowSuccess(true);
      
      // Navigate to verification page after 2 seconds
      setTimeout(() => {
        navigate('/password-recovery-verification', {
          state: { email, accountType }
        });
      }, 2000);
    }, 1500);
  };

  const handleBackToLogin = () => {
    if (accountType === 'business') {
      navigate('/business-login');
    } else {
      navigate('/user-login');
    }
  };

  const colors = accountType === 'business' 
    ? { primary: 'blue', gradient: 'from-blue-50 via-white to-blue-50' }
    : { primary: 'emerald', gradient: 'from-emerald-50 via-white to-emerald-50' };

  if (showSuccess) {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${colors?.gradient} flex items-center justify-center p-4`}>
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className={`w-20 h-20 bg-${colors?.primary}-100 rounded-full flex items-center justify-center mx-auto mb-6`}>
              <Icon name="CheckCircle" size={40} className={`text-${colors?.primary}-600`} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              ¡Código Enviado!
            </h2>
            <p className="text-gray-600 mb-2">
              Hemos enviado un código de verificación a:
            </p>
            <p className={`text-${colors?.primary}-600 font-semibold mb-6`}>
              {email}
            </p>
            <p className="text-sm text-gray-500">
              Redirigiendo a la página de verificación...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br ${colors?.gradient} flex items-center justify-center p-4`}>
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="mb-8 text-center">
          <button
            onClick={handleBackToLogin}
            className={`inline-flex items-center gap-2 text-gray-600 hover:text-${colors?.primary}-600 transition-colors mb-6`}
          >
            <Icon name="ArrowLeft" size={20} />
            <span>Volver al inicio de sesión</span>
          </button>
          
          <div className={`w-20 h-20 bg-${colors?.primary}-600 rounded-2xl flex items-center justify-center mx-auto mb-4`}>
            <Icon name="KeyRound" size={40} color="#FFFFFF" />
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Recuperar Contraseña
          </h1>
          <p className="text-gray-600">
            Ingresa tu correo electrónico y te enviaremos un código de verificación
          </p>
        </div>

        {/* Recovery Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Security Badge */}
          <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-4 mb-6">
            <Icon name="Shield" size={24} className={`text-${colors?.primary}-600`} />
            <div>
              <p className="text-sm font-medium text-gray-900">Proceso Seguro</p>
              <p className="text-xs text-gray-600">Tus datos están protegidos</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Correo Electrónico
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Icon name="Mail" size={20} className="text-gray-400" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e?.target?.value);
                    setError('');
                  }}
                  placeholder={accountType === 'business' ? 'empresa@ejemplo.com' : 'tucorreo@ejemplo.com'}
                  className={`w-full pl-10 pr-4 py-3 border ${
                    error ? 'border-red-500' : 'border-gray-300'
                  } rounded-lg focus:outline-none focus:ring-2 focus:ring-${colors?.primary}-500`}
                />
              </div>
              {error && (
                <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                  <Icon name="AlertCircle" size={16} />
                  {error}
                </p>
              )}
            </div>

            {/* Information Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex gap-3">
                <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-900">
                  <p className="font-medium mb-1">¿Qué sucederá después?</p>
                  <ul className="space-y-1 text-blue-800">
                    <li>• Recibirás un código de 6 dígitos</li>
                    <li>• El código expira en 15 minutos</li>
                    <li>• Revisa tu carpeta de spam si no lo ves</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full bg-${colors?.primary}-600 hover:bg-${colors?.primary}-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isLoading ? (
                <>
                  <Icon name="Loader2" size={20} className="animate-spin" />
                  <span>Enviando código...</span>
                </>
              ) : (
                <>
                  <span>Enviar Código</span>
                  <Icon name="Send" size={20} />
                </>
              )}
            </button>
          </form>

          {/* Alternative Contact */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-600 text-center mb-3">
              ¿No tienes acceso a tu correo?
            </p>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Icon name="Phone" size={16} className={`text-${colors?.primary}-600`} />
                <span className="text-gray-700">Teléfono:</span>
                <a href="tel:+573152495959" className={`text-${colors?.primary}-600 font-medium hover:underline`}>
                  +57 315 249 5959
                </a>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Icon name="Mail" size={16} className={`text-${colors?.primary}-600`} />
                <span className="text-gray-700">Email:</span>
                <a href="mailto:bizznet.latam@gmail.com" className={`text-${colors?.primary}-600 font-medium hover:underline`}>
                  bizznet.latam@gmail.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordRecoveryRequest;